/**
 * useTodoUpdateMutation — Mutation hook for updating todo text content.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuth } from "@/hooks/useAuth";

export const useTodoUpdateMutation = () => {
  const { handleError, handleSuccess } = useErrorHandler();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const updateTodoTextMutation = useMutation({
    mutationFn: async ({ todoId, newText }: { todoId: string, newText: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { data, error } = await supabase
        .from('todos')
        .update({ text: newText })
        .eq('id', todoId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      handleSuccess("Task text has been updated successfully.", { title: "Task updated" });
    },
    onError: (error: Error) => {
      handleError(error, { title: "Error updating task" });
    }
  });

  return {
    updateTodoText: updateTodoTextMutation.mutate,
  };
};
